import type { Decimal, MasterAddress, MasterErrorBody, MasterMovement, MasterPair, ToCardEstimate } from '@/api/masterAccount';
import { railNetworkLabel } from '@/api/topupOptions';
import { formatDisplayAmount, parseAmountInput } from '@/utils/money';
import { isRecoverableError } from '@/utils/apiError';

/**
 * Pure rules of the master-account screens («Общий баланс», docs/cabinet-redesign/04 §8): status chips, amounts,
 * the debounced estimate and the per-tap Idempotency-Key. Functions return KEYS and values, never rendered text —
 * `t()` belongs to the component.
 */

export type ChipTone = 'pending' | 'done' | 'blocked' | 'neutral';

/** The DsStatusChip tone of a movement: in flight — pending; on the balance or the card — done; frozen — blocked. */
export function movementTone(m: Pick<MasterMovement, 'kind' | 'state'>): ChipTone {
  if (m.kind === 'ARRIVAL') {
    switch (m.state) {
      case 'AVAILABLE': return 'done';
      case 'ON_REVIEW': return 'pending';
      case 'FROZEN': return 'blocked';
      case 'RETURNED': return 'neutral';
    }
  } else {
    switch (m.state) {
      case 'QUEUED':
      case 'WAITING_CARD':
      case 'SENDING':
      case 'TO_CARD': return 'pending';
      case 'CREDITED': return 'done';
      case 'CANCELLED':
      case 'RETURNED': return 'neutral';
    }
  }
  return 'neutral';
}

/** i18n key of a movement's status chip. */
export function movementStateKey(m: Pick<MasterMovement, 'kind' | 'state'>): string {
  return m.kind === 'ARRIVAL' ? `cabinet.master.arrival.${m.state}` : `cabinet.master.toCard.${m.state}`;
}

/** Arrivals add to the balance, «to card» takes from it — the sign the row shows. */
export function movementSign(m: Pick<MasterMovement, 'kind'>): 1 | -1 {
  return m.kind === 'ARRIVAL' ? 1 : -1;
}

/**
 * The movement did not change the balance in the end: a «to card» CANCELLED before any money moved, or RETURNED to the
 * shared balance; an arrival RETURNED to its sender. Such a row is never a debit or a credit — it shows its amount
 * unsigned and struck through (the design system's reversed row), beside the «Отменено» / «Возвращается» chip.
 */
export function movementVoid(m: Pick<MasterMovement, 'kind' | 'state'>): boolean {
  if (m.kind === 'ARRIVAL') return m.state === 'RETURNED';
  return m.state === 'CANCELLED' || m.state === 'RETURNED';
}

/** The row's amount text: «+ 120,00 USDT», «− 100,00 USDT», or the bare «50,00 USDT» of a {@link movementVoid} row. */
export function movementAmountText(m: Pick<MasterMovement, 'kind' | 'state' | 'amount' | 'asset'>,
                                   locale?: string): string {
  const v = `${formatDisplayAmount(m.amount, m.asset, locale)} ${m.asset}`;
  if (movementVoid(m)) return v;
  return `${movementSign(m) > 0 ? '+' : '−'} ${v}`;
}

/** «USDT · TRC-20» — the coin and the network as the client reads them. */
export function pairLabel(p: MasterPair): string {
  return `${p.asset.toUpperCase()} · ${railNetworkLabel(p.network)}`;
}

export function samePair(a: MasterPair, b: MasterPair): boolean {
  return a.asset.toUpperCase() === b.asset.toUpperCase() && a.network.toUpperCase() === b.network.toUpperCase();
}

/** A decimal from the wire as a number (0 for anything unreadable) — for comparisons only, never for display. */
export function decimalValue(v: Decimal | null | undefined): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The address «На карту» takes from by default: the one with the most available money (ties — the first, i.e. the
 * server's order). Null when nothing is open.
 */
export function defaultSource(addresses: ReadonlyArray<MasterAddress>): MasterAddress | null {
  let best: MasterAddress | null = null;
  for (const a of addresses) {
    if (!best || decimalValue(a.available) > decimalValue(best.available)) best = a;
  }
  return best;
}

/**
 * The params of «Получили {amount}. Суммы меньше {min} зачисляются, когда на адрес придёт ещё, или через {days} дн.»
 * (vitrine.topup.deposit.belowFloor) — shown only once the client actually sent less than the rail's floor, never up
 * front. `received` is what waits under the floor (the master address's `belowFloor`, a one-off request's `received` on
 * BELOW_MINIMUM), `minUsd` the floor and `days` the server's `belowMinHoldDays`. Null — nothing to say: no arrival, an
 * arrival at or above the floor, or the server named no floor or hold.
 */
export function belowFloorParams(received: Decimal | null | undefined, asset: string,
                                 minUsd: Decimal | null | undefined, days: number | null | undefined,
                                 locale?: string): { amount: string; min: string; days: number } | null {
  if (received == null || minUsd == null || !days) return null;
  const got = decimalValue(received);
  if (!(got > 0) || got >= decimalValue(minUsd)) return null;
  return {
    amount: `${formatDisplayAmount(received, asset, locale)} ${asset}`,
    // The floor is in USD, and every coin the rail takes is a USD stablecoin: name it in the coin the client sent.
    min: `${formatDisplayAmount(minUsd, asset, locale)} ${asset}`,
    days,
  };
}

/** Cents are the finest step: every coin the rail takes is a USD stablecoin. */
export const AMOUNT_SCALE = 2;

/**
 * The amount typed into «Сумма перевода» as the plain decimal the server takes, or null when it is not a positive
 * amount of at most two decimals.
 */
export function toCardAmount(input: string): string | null {
  const plain = parseAmountInput(input);
  if (!/^\d+(\.\d+)?$/.test(plain)) return null;
  const [, frac = ''] = plain.split('.');
  if (frac.length > AMOUNT_SCALE) return null;
  if (Number(plain) <= 0) return null;
  return plain.replace(/^0+(?=\d)/, '');
}

/**
 * The «to card» minimum the sheet names before submit (E2E 31.10 Н-5): the server's own value — the shared-balance
 * view's `toCardMinimum` (the rail's screening floor, USD = every coin), else the estimate's `minimum` — never a number
 * of the frontend. Null when the server reported none.
 */
export function toCardMinimum(viewMinimum: Decimal | null | undefined,
                              estimateMinimum?: Decimal | null): Decimal | null {
  if (viewMinimum !== null && viewMinimum !== undefined && decimalValue(viewMinimum) > 0) return viewMinimum;
  if (estimateMinimum !== null && estimateMinimum !== undefined && decimalValue(estimateMinimum) > 0) return estimateMinimum;
  return null;
}

/** The typed amount (a plain decimal from {@link toCardAmount}) is under the minimum: refused before it is sent. */
export function belowToCardMinimum(amount: string | null, minimum: Decimal | null): boolean {
  if (!amount || minimum === null) return false;
  return Number(amount) < decimalValue(minimum);
}

/** What the estimate block shows: the lines that exist, the pending note, the shortfall. */
export interface EstimateLines {
  toCard: Decimal | null;
  fee: Decimal | null;
  cardPrice: Decimal | null;
  pending: boolean;
  insufficient: boolean;
}

export function estimateLines(e: ToCardEstimate): EstimateLines {
  return {
    toCard: e.pending ? null : e.toCard,
    fee: e.pending ? null : e.fee,
    cardPrice: e.cardPrice !== null && decimalValue(e.cardPrice) > 0 ? e.cardPrice : null,
    pending: e.pending,
    insufficient: !e.sufficient,
  };
}

// ─── the debounced estimate ─────────────────────────────────────────────────────

export interface EstimateRequest {
  cardId: string | null;
  /** A group member's card (docs/cabinet-redesign/07) — priced by their tariff; absent for one's own. */
  recipientId?: string | null;
  network: string;
  asset: string;
  amount: string;
}

export interface EstimateScheduler {
  /** Ask for this input after the pause; a newer call within the pause replaces it. Null input — clear. */
  schedule(input: EstimateRequest | null): void;
  /** Drop the pending call and ignore any answer in flight. */
  cancel(): void;
}

/**
 * Debounces the «придёт на карту» estimate as the client types and keeps only the LATEST answer: an older request
 * that comes back after a newer one was sent is dropped, so the block never shows the price of a previous amount.
 */
export function createEstimateScheduler<R>(opts: {
  delayMs: number;
  fetch: (input: EstimateRequest, signal: AbortSignal) => Promise<R>;
  onResult: (result: R | null) => void;
  onError: (error: unknown) => void;
  onLoading?: (loading: boolean) => void;
}): EstimateScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let seq = 0;
  let controller: AbortController | null = null;

  function cancel(): void {
    if (timer) { clearTimeout(timer); timer = null; }
    seq += 1;
    controller?.abort();
    controller = null;
    opts.onLoading?.(false);
  }

  function schedule(input: EstimateRequest | null): void {
    cancel();
    if (!input) { opts.onResult(null); return; }
    const mine = seq;
    opts.onLoading?.(true);
    timer = setTimeout(() => {
      timer = null;
      controller = new AbortController();
      opts.fetch(input, controller.signal).then(
        r => { if (mine === seq) { opts.onLoading?.(false); opts.onResult(r); } },
        e => { if (mine === seq) { opts.onLoading?.(false); opts.onError(e); } },
      );
    }, opts.delayMs);
  }

  return { schedule, cancel };
}

// ─── one tap, one Idempotency-Key ───────────────────────────────────────────────

/** The command a key is bound to: a different command never reuses a key (the server would answer KEY_REUSED). */
export function commandFingerprint(c: { cardId?: string | null; network: string; asset: string; amount: string;
                                        recipientId?: string | null }): string {
  const parts = [c.cardId ?? '', c.network.toUpperCase(), c.asset.toUpperCase(), c.amount];
  if (c.recipientId) parts.push(c.recipientId);         // a member's card is another command than one's own
  return parts.join('|');
}

/** The factors a 403 STEP_UP_REQUIRED offered, or null for any other answer. */
export function stepUpFactors<F extends string = string>(e: unknown): F[] | null {
  const res = (e as { response?: { status?: number; data?: { error?: unknown; factors?: unknown } } } | null)?.response;
  if (res?.status !== 403 || res.data?.error !== 'STEP_UP_REQUIRED') return null;
  return Array.isArray(res.data.factors) ? (res.data.factors as F[]) : [];
}

/**
 * The Idempotency-Key of «Перевести на карту»: a fresh UUID per tap, the SAME one when the tap is retried after a
 * failure that may not have reached the server (network, timeout, 502–504) — the server then answers with the same
 * request instead of creating a second one. A definite answer (success or a refusal with a code) closes the tap; a
 * changed command (another amount, pair or card) always gets a new key.
 */
export class TapIdempotency {
  private key: string | null = null;
  private fingerprint: string | null = null;

  constructor(private readonly newKey: () => string = () => crypto.randomUUID()) {}

  /** The key for this tap of `fingerprint`: the open one when it is a retry of the same command, else a new one. */
  keyFor(fingerprint: string): string {
    if (this.key && this.fingerprint === fingerprint) return this.key;
    this.key = this.newKey();
    this.fingerprint = fingerprint;
    return this.key;
  }

  /** The tap ended: success, or a failure that did or did not reach the server. */
  settle(error?: unknown): void {
    if (error !== undefined && isRecoverableError(error)) return;   // may not have arrived — keep it for the retry
    this.key = null;
    this.fingerprint = null;
  }

  /** A retry of the open tap is possible (the last failure kept the key). */
  get pending(): boolean {
    return this.key !== null;
  }
}

/** The business code of a refusal (`error` field of the vitrine's error body), or null. */
export function refusalCode(e: unknown): string | null {
  const data = (e as { response?: { data?: { error?: unknown } } } | null)?.response?.data;
  return typeof data?.error === 'string' ? data.error : null;
}

/** MASTER_AMOUNT_BELOW_MIN with its minimum and coin, or null for any other refusal. */
export function belowMinimum(e: unknown): { minimum: Decimal; asset: string } | null {
  const data = (e as { response?: { data?: Partial<MasterErrorBody> } } | null)?.response?.data;
  if (data?.error !== 'MASTER_AMOUNT_BELOW_MIN' || data.minimum === undefined || data.minimum === null) return null;
  return { minimum: data.minimum, asset: data.asset ?? '' };
}
